import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { customerService } from '../../services/customerService';
import type { CustomerActivity } from '../../types/customer360';
import {
  Clock,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  CheckSquare,
  AlertCircle,
  Ticket,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  PhoneIncoming,
  PhoneOutgoing
} from 'lucide-react';
import ActivityModal from './ActivityModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface ActivityTimelineProps {
  customerId: number;
  limit?: number;
}

const activityIcons = {
  note: MessageSquare,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  task: CheckSquare,
  system: AlertCircle,
  ticket: Ticket,
  other: Clock
};

const activityColors = {
  note: 'text-blue-500',
  call: 'text-green-500',
  email: 'text-purple-500',
  meeting: 'text-orange-500',
  task: 'text-yellow-500',
  system: 'text-gray-500',
  ticket: 'text-red-500',
  other: 'text-gray-400'
};

export default function ActivityTimeline({ customerId, limit = 20 }: ActivityTimelineProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [activities, setActivities] = useState<CustomerActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<CustomerActivity | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activityPendingDelete, setActivityPendingDelete] = useState<CustomerActivity | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, [customerId, page]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await customerService.getCustomerActivities(customerId, {
        page,
        limit,
        sortBy: 'activity_date',
        sortOrder: 'desc'
      });

      setActivities(response.activities);
      setHasMore(response.page < response.totalPages);
    } catch (error) {
      console.error('Error fetching activities:', error);
      showToast('Failed to load activities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = () => {
    setEditingActivity(null);
    setShowActivityModal(true);
  };

  const handleEditActivity = (activity: CustomerActivity) => {
    setEditingActivity(activity);
    setShowActivityModal(true);
  };

  const requestDeleteActivity = (activity: CustomerActivity) => {
    setActivityPendingDelete(activity);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setActivityPendingDelete(null);
  };

  const handleDeleteActivity = async () => {
    if (!activityPendingDelete) return;

    try {
      setIsDeletingActivity(true);
      await customerService.deleteActivity(activityPendingDelete.id);
      showToast('Activity deleted successfully', 'success');
      fetchActivities();
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting activity:', error);
      showToast('Failed to delete activity', 'error');
    } finally {
      setIsDeletingActivity(false);
    }
  };

  const handleActivitySaved = () => {
    fetchActivities();
    setShowActivityModal(false);
    setEditingActivity(null);
  };

  const toggleExpanded = (activityId: number) => {
    setExpandedActivities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          Activity Timeline
        </h3>
        <button
          onClick={handleCreateActivity}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Activity
        </button>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {activities.length === 0 ? (
          <div
            className={`text-center py-12 rounded-lg border-2 border-dashed ${
              isDark ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
            }`}
          >
            <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No activities yet</p>
            <p className="text-sm mt-1">Create your first activity to start tracking customer interactions</p>
          </div>
        ) : (
          activities.map((activity, index) => {
            const Icon = activityIcons[activity.activity_type];
            const isExpanded = expandedActivities.has(activity.id);
            const hasLongDescription = activity.description && activity.description.length > 150;

            return (
              <div
                key={activity.id}
                className={`relative pl-8 pb-8 ${
                  index < activities.length - 1 
                    ? isDark ? 'border-l-2 border-gray-700' : 'border-l-2 border-gray-300'
                    : ''
                }`}
              >
                {/* Icon Circle */}
                <div
                  className={`absolute left-0 top-0 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center ${
                    activityColors[activity.activity_type]
                  } ${isDark ? 'bg-gray-800' : 'bg-white'} border-2 ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>

                {/* Activity Card */}
                <div
                  className={`rounded-lg p-4 shadow-sm border ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold capitalize ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {activity.activity_type}
                        </span>
                        {activity.direction && (
                          <span
                            className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                              isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {activity.direction === 'inbound' ? (
                              <PhoneIncoming className="h-3 w-3" />
                            ) : (
                              <PhoneOutgoing className="h-3 w-3" />
                            )}
                            {activity.direction}
                          </span>
                        )}
                        {activity.duration_minutes && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {activity.duration_minutes} min
                          </span>
                        )}
                      </div>
                      {activity.subject && (
                        <h4 className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          {activity.subject}
                        </h4>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditActivity(activity)}
                        className={`p-1 rounded transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                        }`}
                        title="Edit activity"
                      >
                        <Edit2 className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                      </button>
                      <button
                        onClick={() => requestDeleteActivity(activity)}
                        className={`p-1 rounded transition-colors ${
                          isDark ? 'hover:bg-red-900/20' : 'hover:bg-red-100'
                        }`}
                        title="Delete activity"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  {activity.description && (
                    <div className="mb-3">
                      <p
                        className={`text-sm whitespace-pre-wrap ${
                          isDark ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {isExpanded ? activity.description : truncateText(activity.description)}
                      </p>
                      {hasLongDescription && (
                        <button
                          onClick={() => toggleExpanded(activity.id)}
                          className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 mt-2"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="h-4 w-4" />
                              Show less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              Show more
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Outcome */}
                  {activity.outcome && (
                    <div
                      className={`text-sm px-3 py-2 rounded mb-3 ${
                        isDark ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <span className="font-medium">Outcome:</span> {activity.outcome}
                    </div>
                  )}

                  {/* Footer */}
                  <div
                    className={`flex items-center gap-4 text-xs pt-3 border-t ${
                      isDark ? 'text-gray-400 border-gray-700' : 'text-gray-600 border-gray-200'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(activity.activity_date)} at {formatTime(activity.activity_date)}
                    </span>
                    {activity.created_by_name && (
                      <span>by {activity.created_by_name}</span>
                    )}
                    {activity.related_ticket_id && (
                      <span className={`px-2 py-0.5 rounded ${
                        isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        Ticket #{activity.related_ticket_id}
                      </span>
                    )}
                  </div>

                  {/* Attachments */}
                  {activity.attachments && activity.attachments.length > 0 && (
                    <div
                      className={`mt-3 pt-3 border-t ${
                        isDark ? 'border-gray-700' : 'border-gray-200'
                      }`}
                    >
                      <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Attachments ({activity.attachments.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {activity.attachments.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              isDark 
                                ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' 
                                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                            }`}
                          >
                            {attachment.filename}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={() => setPage(page + 1)}
            disabled={loading}
            className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              isDark
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen && !!activityPendingDelete}
        title="Delete Activity"
        description={
          activityPendingDelete ? (
            <>
              Are you sure you want to delete the activity <strong>{activityPendingDelete.subject || activityPendingDelete.activity_type}</strong>?
              This action cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete Activity"
        isProcessing={isDeletingActivity}
        onCancel={() => {
          if (isDeletingActivity) return;
          closeDeleteModal();
        }}
        onConfirm={handleDeleteActivity}
        previewBadge={activityPendingDelete ? {
          label: activityPendingDelete.activity_type,
          color: '#EF4444'
        } : undefined}
      />

      {/* Activity Modal */}
      {showActivityModal && (
        <ActivityModal
          customerId={customerId}
          activity={editingActivity}
          onClose={() => {
            setShowActivityModal(false);
            setEditingActivity(null);
          }}
          onSave={handleActivitySaved}
        />
      )}
    </div>
  );
}
